/**
 * Acessos aos Ambientes: onde o time encontra a URL de cada ambiente e as contas de
 * teste para entrar — o alpha, o /admin de dev, o que mais aparecer.
 *
 * <p>Três decisões de tela valem registro:
 *
 * <p><b>A senha nasce escondida e se revela por clique.</b> Não é segurança — quem vê
 * a página já passou pela sessão —, é higiene de tela compartilhada: a página aberta
 * numa call não mostra as senhas de todo mundo no projetor.
 *
 * <p><b>Copiar é o gesto principal</b>, não selecionar: usuário e senha têm botão
 * próprio, porque o destino deles é sempre um formulário de login em outra aba.
 *
 * <p><b>O link abre com rel="noreferrer"</b>: o destino é ambiente nosso, mas a URL é
 * dado editável — a página nova não precisa saber de onde veio nem ganhar um handle
 * de volta (noreferrer implica noopener).
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { TituloDaPagina } from '../../shell/TituloDaPagina';
import * as api from './acessosApi';
import type { Acesso, Ambiente, Pagina } from './acessosApi';
import { CHAVE_ACESSOS } from './acessosApi';
import './acessos.css';

const RASCUNHO_AMBIENTE = { nome: '', url: '', nota: '' };
const RASCUNHO_ACESSO = { rotulo: '', usuario: '', senha: '', nota: '' };

export function Acessos() {
  const fila = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);

  /** Qual formulário está aberto: o de ambiente novo, ou o de edição de um deles. */
  const [criandoAmbiente, setCriandoAmbiente] = useState(false);
  const [editandoAmbiente, setEditandoAmbiente] = useState<number | null>(null);
  const [rascunhoAmbiente, setRascunhoAmbiente] = useState(RASCUNHO_AMBIENTE);

  /** Em qual ambiente uma conta nova está sendo escrita, e qual conta está em edição. */
  const [criandoAcessoEm, setCriandoAcessoEm] = useState<number | null>(null);
  const [editandoAcesso, setEditandoAcesso] = useState<number | null>(null);
  const [rascunhoAcesso, setRascunhoAcesso] = useState(RASCUNHO_ACESSO);

  const { data, isPending, error } = useQuery<Pagina>({
    queryKey: CHAVE_ACESSOS,
    queryFn: api.listar,
  });

  const aoMudar = {
    onSuccess: () => {
      void fila.invalidateQueries({ queryKey: CHAVE_ACESSOS });
      fecharFormularios();
    },
    onError: (e: Error) => setErro(e.message),
  };

  const criarAmbiente = useMutation({
    mutationFn: () =>
      api.criarAmbiente({
        nome: rascunhoAmbiente.nome,
        url: rascunhoAmbiente.url,
        nota: rascunhoAmbiente.nota || undefined,
      }),
    ...aoMudar,
  });
  const salvarAmbiente = useMutation({
    mutationFn: (id: number) => api.salvarAmbiente(id, rascunhoAmbiente),
    ...aoMudar,
  });
  const excluirAmbiente = useMutation({ mutationFn: api.excluirAmbiente, ...aoMudar });

  const criarAcesso = useMutation({
    mutationFn: (ambienteId: number) =>
      api.criarAcesso(ambienteId, {
        rotulo: rascunhoAcesso.rotulo || undefined,
        usuario: rascunhoAcesso.usuario,
        senha: rascunhoAcesso.senha || undefined,
        nota: rascunhoAcesso.nota || undefined,
      }),
    ...aoMudar,
  });
  const salvarAcesso = useMutation({
    mutationFn: (id: number) => api.salvarAcesso(id, rascunhoAcesso),
    ...aoMudar,
  });
  const excluirAcesso = useMutation({ mutationFn: api.excluirAcesso, ...aoMudar });

  function fecharFormularios() {
    setCriandoAmbiente(false);
    setEditandoAmbiente(null);
    setCriandoAcessoEm(null);
    setEditandoAcesso(null);
    setRascunhoAmbiente(RASCUNHO_AMBIENTE);
    setRascunhoAcesso(RASCUNHO_ACESSO);
    setErro(null);
  }

  if (isPending) return <div className="rm-estado">carregando os acessos…</div>;
  if (error) return <div className="rm-estado">não consegui carregar: {error.message}</div>;

  return (
    <div className="acessos">
      <TituloDaPagina>
        URLs e contas de teste de cada ambiente — só senha de teste entra aqui; a pessoal e
        a de produção, nunca.
      </TituloDaPagina>

      {erro && <p className="ac-erro">{erro}</p>}

      {data.ambientes.length === 0 && !criandoAmbiente && (
        <p className="ac-vazio">
          Nenhum ambiente cadastrado ainda. O primeiro costuma ser o alpha do jogo.
        </p>
      )}

      {data.ambientes.map((ambiente) => (
        <CartaoDeAmbiente
          key={ambiente.id}
          ambiente={ambiente}
          emEdicao={editandoAmbiente === ambiente.id}
          rascunho={rascunhoAmbiente}
          aoRascunhar={setRascunhoAmbiente}
          aoEditar={() => {
            fecharFormularios();
            setEditandoAmbiente(ambiente.id);
            setRascunhoAmbiente({
              nome: ambiente.nome,
              url: ambiente.url,
              nota: ambiente.nota ?? '',
            });
          }}
          aoSalvar={() => salvarAmbiente.mutate(ambiente.id)}
          aoExcluir={() => {
            if (
              confirm(
                `Excluir o ambiente "${ambiente.nome}"?\nAs contas de teste dele vão junto.`
              )
            )
              excluirAmbiente.mutate(ambiente.id);
          }}
          aoCancelar={fecharFormularios}
          criandoConta={criandoAcessoEm === ambiente.id}
          contaEmEdicao={editandoAcesso}
          rascunhoConta={rascunhoAcesso}
          aoRascunharConta={setRascunhoAcesso}
          aoAbrirContaNova={() => {
            fecharFormularios();
            setCriandoAcessoEm(ambiente.id);
          }}
          aoEditarConta={(c) => {
            fecharFormularios();
            setEditandoAcesso(c.id);
            setRascunhoAcesso({
              rotulo: c.rotulo ?? '',
              usuario: c.usuario,
              senha: c.senha ?? '',
              nota: c.nota ?? '',
            });
          }}
          aoCriarConta={() => criarAcesso.mutate(ambiente.id)}
          aoSalvarConta={(id) => salvarAcesso.mutate(id)}
          aoExcluirConta={(c) => {
            if (confirm(`Excluir a conta "${c.usuario}" de ${ambiente.nome}?`))
              excluirAcesso.mutate(c.id);
          }}
        />
      ))}

      {criandoAmbiente ? (
        <section className="ac-cartao ac-formulario">
          <h3>Ambiente novo</h3>
          <FormularioDeAmbiente rascunho={rascunhoAmbiente} aoMudar={setRascunhoAmbiente} />
          <div className="ac-acoes">
            <button type="button" className="ac-primario" onClick={() => criarAmbiente.mutate()}>
              Criar ambiente
            </button>
            <button type="button" onClick={fecharFormularios}>
              Cancelar
            </button>
          </div>
        </section>
      ) : (
        <button
          type="button"
          className="ac-novo-ambiente"
          onClick={() => {
            fecharFormularios();
            setCriandoAmbiente(true);
          }}
        >
          + ambiente
        </button>
      )}
    </div>
  );
}

function CartaoDeAmbiente({
  ambiente,
  emEdicao,
  rascunho,
  aoRascunhar,
  aoEditar,
  aoSalvar,
  aoExcluir,
  aoCancelar,
  criandoConta,
  contaEmEdicao,
  rascunhoConta,
  aoRascunharConta,
  aoAbrirContaNova,
  aoEditarConta,
  aoCriarConta,
  aoSalvarConta,
  aoExcluirConta,
}: {
  ambiente: Ambiente;
  emEdicao: boolean;
  rascunho: typeof RASCUNHO_AMBIENTE;
  aoRascunhar(r: typeof RASCUNHO_AMBIENTE): void;
  aoEditar(): void;
  aoSalvar(): void;
  aoExcluir(): void;
  aoCancelar(): void;
  criandoConta: boolean;
  contaEmEdicao: number | null;
  rascunhoConta: typeof RASCUNHO_ACESSO;
  aoRascunharConta(r: typeof RASCUNHO_ACESSO): void;
  aoAbrirContaNova(): void;
  aoEditarConta(c: Acesso): void;
  aoCriarConta(): void;
  aoSalvarConta(id: number): void;
  aoExcluirConta(c: Acesso): void;
}) {
  if (emEdicao) {
    return (
      <section className="ac-cartao ac-formulario">
        <h3>Editar ambiente</h3>
        <FormularioDeAmbiente rascunho={rascunho} aoMudar={aoRascunhar} />
        <div className="ac-acoes">
          <button type="button" className="ac-primario" onClick={aoSalvar}>
            Salvar
          </button>
          <button type="button" onClick={aoCancelar}>
            Cancelar
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="ac-cartao">
      <header className="ac-cabecalho">
        <div>
          <h3>{ambiente.nome}</h3>
          {/* noreferrer implica noopener: a aba nova não ganha handle de volta. */}
          <a className="ac-url" href={ambiente.url} target="_blank" rel="noreferrer">
            {ambiente.url} ↗
          </a>
          {ambiente.nota && <p className="ac-nota">{ambiente.nota}</p>}
        </div>
        <div className="ac-ferramentas">
          <button type="button" title="Editar ambiente" onClick={aoEditar}>
            ✎
          </button>
          <button type="button" title="Excluir ambiente" onClick={aoExcluir}>
            ×
          </button>
        </div>
      </header>

      {ambiente.acessos.length > 0 && (
        <table className="ac-contas">
          <thead>
            <tr>
              <th>Conta</th>
              <th>Usuário</th>
              <th>Senha</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {ambiente.acessos.map((c) =>
              contaEmEdicao === c.id ? (
                <tr key={c.id} className="ac-linha-formulario">
                  <td colSpan={4}>
                    <FormularioDeConta rascunho={rascunhoConta} aoMudar={aoRascunharConta} />
                    <div className="ac-acoes">
                      <button
                        type="button"
                        className="ac-primario"
                        onClick={() => aoSalvarConta(c.id)}
                      >
                        Salvar
                      </button>
                      <button type="button" onClick={aoCancelar}>
                        Cancelar
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={c.id}>
                  <td className="ac-rotulo">{c.rotulo ?? '—'}</td>
                  <td>
                    <span className="ac-valor">{c.usuario}</span>
                    <BotaoCopiar valor={c.usuario} oQue="usuário" />
                  </td>
                  <td>
                    {c.senha ? (
                      <>
                        <SenhaRevelavel valor={c.senha} />
                        <BotaoCopiar valor={c.senha} oQue="senha" />
                      </>
                    ) : (
                      <span className="ac-sem-senha">— sem senha (login social) —</span>
                    )}
                  </td>
                  <td className="ac-ferramentas">
                    {c.nota && <span title={c.nota}>ℹ</span>}
                    <button type="button" title="Editar conta" onClick={() => aoEditarConta(c)}>
                      ✎
                    </button>
                    <button
                      type="button"
                      title="Excluir conta"
                      onClick={() => aoExcluirConta(c)}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      )}

      {criandoConta ? (
        <div className="ac-linha-formulario">
          <FormularioDeConta rascunho={rascunhoConta} aoMudar={aoRascunharConta} />
          <div className="ac-acoes">
            <button type="button" className="ac-primario" onClick={aoCriarConta}>
              Adicionar conta
            </button>
            <button type="button" onClick={aoCancelar}>
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="ac-nova-conta" onClick={aoAbrirContaNova}>
          + conta de teste
        </button>
      )}
    </section>
  );
}

function FormularioDeAmbiente({
  rascunho,
  aoMudar,
}: {
  rascunho: typeof RASCUNHO_AMBIENTE;
  aoMudar(r: typeof RASCUNHO_AMBIENTE): void;
}) {
  return (
    <div className="ac-campos">
      <label>
        Nome
        <input
          value={rascunho.nome}
          placeholder="Alphazera"
          onChange={(e) => aoMudar({ ...rascunho, nome: e.target.value })}
        />
      </label>
      <label>
        URL
        <input
          value={rascunho.url}
          placeholder="https://alphazera.mistikisland.com"
          onChange={(e) => aoMudar({ ...rascunho, url: e.target.value })}
        />
      </label>
      <label>
        Nota
        <input
          value={rascunho.nota}
          placeholder="o que alguém precisa saber antes de entrar (opcional)"
          onChange={(e) => aoMudar({ ...rascunho, nota: e.target.value })}
        />
      </label>
    </div>
  );
}

function FormularioDeConta({
  rascunho,
  aoMudar,
}: {
  rascunho: typeof RASCUNHO_ACESSO;
  aoMudar(r: typeof RASCUNHO_ACESSO): void;
}) {
  return (
    <div className="ac-campos">
      <label>
        Conta
        <input
          value={rascunho.rotulo}
          placeholder="Testador comum (opcional)"
          onChange={(e) => aoMudar({ ...rascunho, rotulo: e.target.value })}
        />
      </label>
      <label>
        Usuário
        <input
          value={rascunho.usuario}
          placeholder="teste@goyazgames.com"
          onChange={(e) => aoMudar({ ...rascunho, usuario: e.target.value })}
        />
      </label>
      <label>
        Senha
        <input
          value={rascunho.senha}
          placeholder="em branco se o login é social"
          onChange={(e) => aoMudar({ ...rascunho, senha: e.target.value })}
        />
      </label>
      <label>
        Nota
        <input
          value={rascunho.nota}
          placeholder="ex.: conta com save no capítulo 2 (opcional)"
          onChange={(e) => aoMudar({ ...rascunho, nota: e.target.value })}
        />
      </label>
    </div>
  );
}

/**
 * A senha nasce escondida. Não é segurança — é a página aberta no projetor de uma
 * call não mostrar as senhas de todo mundo. Revelar é por conta, não global.
 */
function SenhaRevelavel({ valor }: { valor: string }) {
  const [visivel, setVisivel] = useState(false);
  return (
    <button
      type="button"
      className="ac-senha"
      title={visivel ? 'Esconder' : 'Revelar'}
      onClick={() => setVisivel(!visivel)}
    >
      {visivel ? valor : '••••••••'}
    </button>
  );
}

function BotaoCopiar({ valor, oQue }: { valor: string; oQue: string }) {
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!copiado) return;
    const volta = setTimeout(() => setCopiado(false), 1500);
    return () => clearTimeout(volta);
  }, [copiado]);

  return (
    <button
      type="button"
      className="ac-copiar"
      title={`Copiar ${oQue}`}
      onClick={() => {
        void navigator.clipboard.writeText(valor).then(() => setCopiado(true));
      }}
    >
      {copiado ? '✓' : '⧉'}
    </button>
  );
}
